import { FunctionalComponent } from 'preact';
import { useEffect } from 'preact/hooks';
import { LocationProvider, ErrorBoundary } from 'preact-iso';
import { NavProvider } from '@accedo/spatial-nav';
import {
  virtualKeyListeners,
  VirtualKeys,
  KeyCodes,
  Priority,
  KeyEventType,
  sendVirtualKeyEvent,
  VirtualKeyboardEvent,
} from '@accedo/virtual-key';
import { LifecycleEvent, LIFECYCLE_EVENT, deviceApi, ExitReasonType } from '@accedo/ctv-device-api';
import { logger } from '@accedo/logger';
import { DialogManager, useDialog } from '@accedo/ctv-ui-dialog';
import { SnackbarManager } from '@accedo/ctv-ui-snackbar';
import { PageNavigationProvider } from '@accedo/page-navigation';
import { onKeydownGlobal } from '##/utils';
import { handleGlobalBackKey } from '##/services/routing';
import { nav } from '##/services/navigation';
import { AppContent } from '##/AppContent';
import { LoadingIndicator } from '##/components/LoadingIndicator/index';
import { MenuProvider } from '##/context/MenuContext';
import { logDeviceInfo } from '##/utils/deviceUtils';
import { FastDomProvider } from '##/context/FastDomContext';
import AuthContextProvider from '##/context/AuthContext';
import GenericPopupProvider from '##/context/GenericPopupContext';
import { SubscriptionProvider } from '##/context/SubscriptionContext';
import MouseProvider from '##/context/MouseContext';

const onGlobalKeyListenerId = 'onGlobalKey';
const onGlobalBackListenerId = 'onGlobalBackKey';
const LP = '[App]';

const App: FunctionalComponent = () => {
  const onLifecycleEvent = (event: CustomEvent): void => {
    const eventDetail: LifecycleEvent = event.detail;
    const { state, previous } = eventDetail;

    logger.info(`${LP} onLifecycleEvent from: ${previous} to ${state}`);
  };

  useEffect(() => {
    const SCREEN_WIDTH_PX = Number(process.env.DEVICE_WIDTH);
    const SCREEN_HEIGHT_PX = Number(process.env.DEVICE_HEIGHT);
    logger.info(`${LP} Width: ${SCREEN_WIDTH_PX}  Height: ${SCREEN_HEIGHT_PX}`);

    // Workstation: Map browser Escape key to back navigation
    let handleEscapeKey: ((event: KeyboardEvent) => void) | null = null;

    const setup = async (): Promise<void> => {
      await logDeviceInfo();

      // For platforms that support lifecycle, e.g firebolt
      if (deviceApi.lifecycle) {
        window.addEventListener(LIFECYCLE_EVENT, onLifecycleEvent as EventListener);
        logger.debug(`${LP} platform ${process.env.APP_PLATFORM} supports lifecycle, calling lifecycle ready`);
        await deviceApi.lifecycle?.ready();
      } else {
        logger.debug(`${LP} NO lifecycle API for this platform ${process.env.APP_PLATFORM}`);
      }

      virtualKeyListeners.enqueue(
        async (keyName: VirtualKeys) => {
          if (keyName === KeyCodes.vKey.BACK) {
            const dialog = useDialog();
            const isDialogOpen = dialog.state.value.isOpen;
            if (isDialogOpen) {
              dialog.close();
            } else {
              handleGlobalBackKey();
            }
            return true;
          }
          if (keyName === KeyCodes.vKey.EXIT) {
            deviceApi.exit(ExitReasonType.USER_EXIT);
            return true;
          }
          return false;
        },
        Priority.LOWER,
        onGlobalBackListenerId,
      );

      // Workstation: Map browser Escape key to back navigation
      if (process.env.APP_PLATFORM === 'workstation') {
        handleEscapeKey = (event: KeyboardEvent): void => {
          if (event.key === 'Escape') {
            // Dispatch through the virtual key queue so priority listeners
            // (e.g. the player at Priority.HIGH) can intercept before the global handler
            sendVirtualKeyEvent({ virtualKey: KeyCodes.vKey.BACK } as VirtualKeyboardEvent);
            event.preventDefault();
            event.stopPropagation();
          }
        };
        window.addEventListener('keydown', handleEscapeKey);
      }

      virtualKeyListeners.enqueue(
        async (keyName: VirtualKeys, eventType: KeyEventType) => {
          if (keyName !== KeyCodes.vKey.BACK) {
            await onKeydownGlobal(keyName, eventType);
            return true;
          }
          return false;
        },
        Priority.LOWEST,
        onGlobalKeyListenerId,
        undefined,
        [KeyCodes.vKey.INFO],
      );

      // Do initial deeplink check here because on first load
      // events may have fired before device package set listeners
      await deviceApi.deeplink?.checkForDeeplink?.();
    };

    setup();

    return async () => {
      logger.debug(`${LP} App unmounting, cleaning up listeners`);
      if (deviceApi.lifecycle) {
        window.removeEventListener(LIFECYCLE_EVENT, onLifecycleEvent as EventListener);
      }
      virtualKeyListeners.removeListenerById(onGlobalBackListenerId);
      virtualKeyListeners.removeListenerById(onGlobalKeyListenerId);
      // Workstation: Remove Escape key listener
      if (handleEscapeKey) {
        window.removeEventListener('keydown', handleEscapeKey);
      }
      // Messaging service removed - no cleanup needed
    };
  }, []);

  logger.debug(`${LP} App render`);
  return (
    <LocationProvider>
      <ErrorBoundary>
        <FastDomProvider>
          <NavProvider nav={nav}>
            <MouseProvider>
              <PageNavigationProvider>
                <AuthContextProvider>
                  <SubscriptionProvider>
                    <GenericPopupProvider>
                      <MenuProvider>
                        <div id="app">
                          <LoadingIndicator showOnMount={false} />
                          <AppContent />
                          <DialogManager />
                          <SnackbarManager />
                        </div>
                      </MenuProvider>
                    </GenericPopupProvider>
                  </SubscriptionProvider>
                </AuthContextProvider>
              </PageNavigationProvider>
            </MouseProvider>
          </NavProvider>
        </FastDomProvider>
      </ErrorBoundary>
    </LocationProvider>
  );
};

export default App;
